/**
 * WaxumAudioBridge — browser side of an in-CRM WhatsApp voice call.
 *
 * Transport: a WebSocket to the whatsapp-service media proxy at
 * `wss://{location.host}/waxum-media/ws` (nginx → whatsapp:3001). The browser
 * NEVER talks to the waxum gateway directly — the proxy holds the Bearer JWT
 * and resolves `account_id → waxum_session_id` server-side.
 *
 * Handshake (first-message auth — keeps the Sanctum token out of URLs and
 * access logs; the proxy closes the socket if it doesn't arrive within 5 s):
 *
 *   client → {type:'auth', token, mode:'outbound'|'answer'|'attach', account_id,
 *             to_jid?, call_id?, waxum_call_id?, lead_id?, user_id?, call_grant?}
 *
 * `call_grant` is the one-shot token POST /whatsapp-calls/outbound|accept
 * returned; for outbound/answer the proxy redeems it and binds the call to
 * what the backend minted (account, callee, lead) rather than to the frame.
 *   server → {"type":"call_started", call_id, sample_rate:16000,
 *             frame_samples:960, encoding:"pcm_s16le_mono_16khz"}   (success)
 *          | {"type":"error", code}                                  (failure)
 *          | {"type":"no_active_call"}                (attach: grace expired)
 *
 * `attach` re-binds a call that is still alive on the sidecar after the tab
 * dropped (page refresh, laptop lid) — the sidecar keeps a dropped call alive
 * for 30 s and re-sends `call_started` on a successful attach.
 *
 * After `call_started`, every frame in BOTH directions is untagged binary:
 * raw little-endian Int16 PCM, mono, 16 kHz, 960 samples (60 ms). Closing the
 * WebSocket hangs the call up (the sidecar is the authoritative lifecycle
 * writer, so the phone_calls row closes even if this tab dies).
 *
 * Audio graph:
 *   getUserMedia → MediaStreamSource → `pcm-capture` worklet → (silent gain)
 *   ws binary → `pcm-playback` worklet (jitter buffer) → destination
 *
 * `getUserMedia` is only ever invoked from `start()`, which callers must run
 * inside a user gesture (Answer / Call click) — otherwise Chrome auto-denies.
 */

const WORKLET_URL = '/worklets/waxum-pcm-processor.js'
const MEDIA_WS_PATH = '/waxum-media/ws'

/** Ringback: 425 Hz, 1 s on / 3 s off (ETSI-ish), quiet enough not to clip. */
const RINGBACK_HZ = 425
const RINGBACK_GAIN = 0.04
/** Mic meter is pushed at most this often — capture fires every 60 ms. */
const MIC_LEVEL_INTERVAL_MS = 150
/** Under this int16 peak for 3 s straight the mic is treated as dead. */
const MIC_SILENT_PEAK = 100
const MIC_SILENT_WINDOW_MS = 3000

export type WaxumCallMode = 'outbound' | 'answer' | 'attach'

/** The `call_started` meta frame the proxy forwards verbatim from waxum. */
export interface WaxumCallMeta {
  type: 'call_started'
  call_id: string
  sample_rate: number
  frame_samples: number
  encoding: string
  [key: string]: unknown
}

export type WaxumBridgeErrorCode =
  /** User (or policy) denied the microphone — NotAllowedError. */
  | 'mic_denied'
  /** No input device, device busy, or getUserMedia unsupported. */
  | 'mic_unavailable'
  /** AudioWorklet / AudioContext unavailable (very old browser, insecure ctx). */
  | 'audio_unsupported'
  /** The media proxy refused the call (auth, membership, upstream dial). */
  | 'server_error'
  /** `attach` only — the 30 s re-attach grace expired, the call is gone. */
  | 'no_active_call'
  /** WebSocket transport failure. */
  | 'ws_error'

/** Typed failure the UI turns into a toast (see WhatsAppCallProvider). */
export class WaxumBridgeError extends Error {
  readonly code: WaxumBridgeErrorCode

  constructor(code: WaxumBridgeErrorCode, message?: string) {
    super(message ?? code)
    this.name = 'WaxumBridgeError'
    this.code = code
  }
}

export interface WaxumAudioBridgeOptions {
  /**
   * 'outbound' places a new call (needs `toJid`); 'answer' attaches to an
   * accepted inbound one (needs `callId`); 'attach' re-binds a call that is
   * still live on the sidecar after this tab dropped (needs `waxumCallId`).
   */
  mode: WaxumCallMode
  /** The same Sanctum token the Socket.IO client authenticates with. */
  token: string
  accountId: string
  /** Outbound only — the destination JID resolved server-side by POST /whatsapp-calls/outbound. */
  toJid?: string
  /** Answer only — the waxum call id from the `call:incoming` socket event. */
  callId?: string
  /** Attach only — the waxum call id from GET /whatsapp-calls/active. */
  waxumCallId?: string
  leadId?: string | null
  userId?: string | null
  /**
   * Outbound/answer — the `call_grant` from the REST response that authorised
   * this call. Required by a sidecar with CALL_GRANT_REQUIRED (the default).
   */
  callGrant?: string | null
  /** Fired once, when the `call_started` meta frame arrives and audio is live. */
  onConnected?: (meta: WaxumCallMeta) => void
  /**
   * Outbound only: the callee's phone has the offer (the gateway's
   * `call_state: ringing`). Never fires with a gateway that does not narrate.
   */
  onRinging?: () => void
  /**
   * Fired once when the callee actually picks up. A narrating gateway says so
   * (`{type:'call_state', state:'answered'}`, or `answered_at` on the meta
   * frame); with an older one we fall back to the first downlink PCM frame,
   * which is EARLY (the engine plays out silence before pickup) — hence the
   * `call_states` capability flag that turns that fallback off.
   */
  onAnswered?: () => void
  /** Mic meter, throttled to ~7 Hz. `silent` = no signal for 3 s (dead mic). */
  onMicLevel?: (level: number, silent: boolean) => void
  /** Fired exactly once when the call is over (any reason), including on stop(). */
  onEnded?: (reason: string) => void
}

export class WaxumAudioBridge {
  private readonly options: WaxumAudioBridgeOptions
  private ws: WebSocket | null = null
  private ctx: AudioContext | null = null
  private stream: MediaStream | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private capture: AudioWorkletNode | null = null
  private playback: AudioWorkletNode | null = null
  private sink: GainNode | null = null
  private ringback: { osc: OscillatorNode; gain: GainNode; timer: ReturnType<typeof setInterval> } | null =
    null
  private muted = false
  private connected = false
  private answered = false
  /** The gateway narrates ringing / answered / ended; PCM is not an answer. */
  private gatewayStates = false
  private stopped = false
  private endReported = false
  private lastMicLevelAt = 0
  private micPeak = 0
  private micWindowStart = 0
  private micSilent = false

  constructor(options: WaxumAudioBridgeOptions) {
    this.options = options
  }

  /** True once the `call_started` meta frame has been received. */
  get isConnected(): boolean {
    return this.connected
  }

  /** True once the far end picked up (explicit state frame or first downlink audio). */
  get isAnswered(): boolean {
    return this.answered
  }

  /** True when the gateway behind this call sends `call_state` frames. */
  get supportsStates(): boolean {
    return this.gatewayStates
  }

  /**
   * Acquire the mic, build the audio graph and open the media socket.
   * MUST be called from a user gesture. Rejects with a {@link WaxumBridgeError}
   * whose `code` the UI maps to a toast; the bridge is fully torn down on
   * rejection (no dangling mic indicator).
   */
  async start(): Promise<void> {
    if (this.stopped) throw new WaxumBridgeError('ws_error', 'bridge already stopped')

    try {
      // 1) Microphone first — while we are still inside the user gesture.
      this.stream = await this.acquireMicrophone()

      // 2) Audio context at the HARDWARE rate; the worklet resamples to 16 kHz.
      this.ctx = this.createAudioContext()
      if (this.ctx.state === 'suspended') {
        await this.ctx.resume().catch(() => undefined)
      }
      await this.ctx.audioWorklet.addModule(WORKLET_URL)

      this.source = this.ctx.createMediaStreamSource(this.stream)
      this.capture = new AudioWorkletNode(this.ctx, 'pcm-capture', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: 1,
      })
      this.playback = new AudioWorkletNode(this.ctx, 'pcm-playback', {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      })

      // The capture node needs a downstream connection or the graph is never
      // pulled; route it into a muted gain so nothing is echoed to the speaker.
      this.sink = this.ctx.createGain()
      this.sink.gain.value = 0
      this.source.connect(this.capture)
      this.capture.connect(this.sink)
      this.sink.connect(this.ctx.destination)
      this.playback.connect(this.ctx.destination)

      this.capture.port.onmessage = (event: MessageEvent) => this.onCaptureFrame(event)
      this.micWindowStart = Date.now()

      // Outbound only: local ringback so a dial isn't silent while the far end
      // rings. Stops the moment we detect an answer (or the call ends).
      if (this.options.mode === 'outbound') this.startRingback()

      // 3) Media socket + auth frame.
      await this.openSocket()
    } catch (error) {
      this.teardown()
      throw error instanceof WaxumBridgeError
        ? error
        : new WaxumBridgeError('ws_error', error instanceof Error ? error.message : String(error))
    }
  }

  /**
   * Mute/unmute the microphone. Muted still sends silence frames at the same
   * 60 ms cadence — waxum's jitter buffer expects a continuous stream, and a
   * gap would be read as a dead leg.
   */
  setMuted(muted: boolean): void {
    this.muted = muted
  }

  get isMuted(): boolean {
    return this.muted
  }

  /**
   * Tear everything down and hang the call up (closing the WS is the hangup
   * signal). Idempotent — safe to call from a useEffect cleanup, from the
   * hangup button and from the socket's own close handler.
   */
  stop(reason = 'local_hangup'): void {
    if (this.stopped) return
    this.stopped = true
    if (reason === 'local_hangup' && this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: 'hangup' }))
      } catch {
        // The close reason still carries the intent if the socket races closed.
      }
    }
    this.teardown(reason === 'local_hangup')
    this.reportEnded(reason)
  }

  // ---------------------------------------------------------------- internals

  private async acquireMicrophone(): Promise<MediaStream> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      throw new WaxumBridgeError('mic_unavailable', 'getUserMedia unavailable (needs HTTPS)')
    }
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
        video: false,
      })
    } catch (error) {
      const name = (error as { name?: string } | null)?.name
      if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
        throw new WaxumBridgeError('mic_denied', 'microphone permission denied')
      }
      throw new WaxumBridgeError('mic_unavailable', name ?? 'microphone unavailable')
    }
  }

  private createAudioContext(): AudioContext {
    const Ctor: typeof AudioContext | undefined =
      typeof window !== 'undefined'
        ? window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
        : undefined
    if (!Ctor) throw new WaxumBridgeError('audio_unsupported', 'AudioContext unavailable')

    // Deliberately NOT `new Ctor({ sampleRate: 16000 })`. Forcing the wire rate
    // would save a resample, but several browser/device combinations accept the
    // constructor and then produce NO AUDIO AT ALL (silent output, silent
    // capture) — a failure mode that is invisible until a real call. Run at the
    // hardware rate and let the worklet linear-resample 48k↔16k both ways.
    return new Ctor()
  }

  // ---------------------------------------------------------------- ringback

  private startRingback(): void {
    const ctx = this.ctx
    if (!ctx || this.ringback) return
    try {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.frequency.value = RINGBACK_HZ
      gain.gain.value = 0
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start()
      // 1 s on / 3 s off. `setTargetAtTime` ramps instead of clicking.
      gain.gain.setTargetAtTime(RINGBACK_GAIN, ctx.currentTime, 0.02)
      let phase = 0
      const timer = setInterval(() => {
        phase = (phase + 1) % 4
        gain.gain.setTargetAtTime(phase === 0 ? RINGBACK_GAIN : 0, ctx.currentTime, 0.02)
      }, 1000)
      this.ringback = { osc, gain, timer }
    } catch {
      // A missing oscillator is never worth failing a call over.
    }
  }

  private stopRingback(): void {
    const ring = this.ringback
    this.ringback = null
    if (!ring) return
    clearInterval(ring.timer)
    try {
      ring.osc.stop()
      ring.osc.disconnect()
      ring.gain.disconnect()
    } catch {
      // ignore
    }
  }

  /** Idempotent answer latch — kills ringback and tells the UI exactly once. */
  private markAnswered(): void {
    if (this.answered) return
    this.answered = true
    this.stopRingback()
    this.options.onAnswered?.()
  }

  private openSocket(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const url =
        process.env.NEXT_PUBLIC_WAXUM_MEDIA_WS_URL ||
        `${protocol}//${window.location.host}${MEDIA_WS_PATH}`

      let ws: WebSocket
      try {
        ws = new WebSocket(url)
      } catch (error) {
        reject(new WaxumBridgeError('ws_error', error instanceof Error ? error.message : 'socket failed'))
        return
      }
      ws.binaryType = 'arraybuffer'
      this.ws = ws

      ws.onopen = () => {
        // First frame within 5 s or the proxy drops us.
        ws.send(
          JSON.stringify({
            type: 'auth',
            token: this.options.token,
            mode: this.options.mode,
            account_id: this.options.accountId,
            ...(this.options.toJid ? { to_jid: this.options.toJid } : {}),
            ...(this.options.callId ? { call_id: this.options.callId } : {}),
            ...(this.options.waxumCallId ? { waxum_call_id: this.options.waxumCallId } : {}),
            ...(this.options.leadId ? { lead_id: this.options.leadId } : {}),
            ...(this.options.userId ? { user_id: this.options.userId } : {}),
            ...(this.options.callGrant ? { call_grant: this.options.callGrant } : {}),
          })
        )
      }

      ws.onmessage = (event: MessageEvent) => {
        if (typeof event.data === 'string') {
          let payload: { type?: string; code?: string } & Record<string, unknown>
          try {
            payload = JSON.parse(event.data)
          } catch {
            return
          }
          if (payload.type === 'call_started') {
            this.connected = true
            if (payload.call_states === true) this.gatewayStates = true
            if (!settled) {
              settled = true
              resolve()
            }
            this.options.onConnected?.(payload as unknown as WaxumCallMeta)
            // Who is already on the line when this socket opens:
            //  - an inbound call we just accepted, always;
            //  - a re-attach (refresh), only if the sidecar says the call was
            //    answered — a refresh mid-ring must not fake an answer. An
            //    older sidecar replays no state at all; keep its old meaning.
            //  - an outbound dial, never: it has a genuine ringing phase.
            if (payload.answered === true || payload.answered_at || payload.state === 'answered') {
              this.markAnswered()
            } else if (this.options.mode === 'answer') {
              this.markAnswered()
            } else if (this.options.mode === 'attach') {
              if (payload.state === 'ringing') {
                this.options.onRinging?.()
              } else if (!('state' in payload)) {
                // Legacy sidecar: no replayed state. `answered: false` meant
                // its PCM heuristic had not fired yet — media up, still
                // "ringing" in the old sense; no flag at all meant answered.
                if (payload.answered === false) this.options.onRinging?.()
                else this.markAnswered()
              }
            }
            return
          }
          // The gateway's narration, relayed verbatim by the sidecar.
          if (payload.type === 'call_state') {
            this.gatewayStates = true
            const state = String(payload.state ?? '')
            if (state === 'ringing' && !this.answered) this.options.onRinging?.()
            if (state === 'answered') this.markAnswered()
            if (state === 'ended') this.stop(`remote_${String(payload.reason ?? 'unknown')}`)
            return
          }
          // The sidecar's own lifecycle relay, when it has one.
          if (payload.type === 'state') {
            const state = String(payload.state ?? '')
            if (state === 'answered' || state === 'in_progress') this.markAnswered()
            if (state === 'ended' || state === 'failed') this.stop(`remote_${state}`)
            return
          }
          // Attach raced the 30 s grace window — the call is genuinely gone.
          if (payload.type === 'no_active_call') {
            const gone = new WaxumBridgeError('no_active_call', 'call already ended')
            if (!settled) {
              settled = true
              reject(gone)
            } else {
              this.stop('no_active_call')
            }
            return
          }
          if (payload.type === 'error') {
            const code = String(payload.code ?? 'error')
            const err = new WaxumBridgeError(
              code === 'no_active_call' ? 'no_active_call' : 'server_error',
              code
            )
            if (!settled) {
              settled = true
              reject(err)
            } else {
              this.stop(`server_error:${code}`)
            }
            return
          }
          return
        }

        // Binary = downlink PCM. Ignore anything before call_started.
        if (!this.connected || !this.playback) return
        const buffer =
          event.data instanceof ArrayBuffer ? (event.data as ArrayBuffer) : null
        if (!buffer || buffer.byteLength === 0) return
        // Fallback for a gateway that does not narrate: audio from the far end
        // is the only signal there is. It is early (silence plays out before
        // pickup), which is why a narrating gateway switches it off.
        if (!this.gatewayStates) this.markAnswered()
        this.playback.port.postMessage(buffer, [buffer])
      }

      ws.onerror = () => {
        if (!settled) {
          settled = true
          reject(new WaxumBridgeError('ws_error', 'media socket error'))
        }
      }

      ws.onclose = (event: CloseEvent) => {
        if (!settled) {
          settled = true
          reject(new WaxumBridgeError('ws_error', `media socket closed (${event.code})`))
          return
        }
        // Remote hangup / proxy teardown — mirror it locally exactly once.
        this.stopped = true
        this.teardown()
        this.reportEnded('remote_hangup')
      }
    })
  }

  private onCaptureFrame(event: MessageEvent): void {
    const buffer = event.data as ArrayBuffer
    if (!(buffer instanceof ArrayBuffer)) return

    // Meter the exact frames we are about to transmit — a meter fed from a
    // separate AnalyserNode can read fine while the transmitted leg is dead.
    this.meterFrame(buffer)

    const ws = this.ws
    if (!ws || ws.readyState !== WebSocket.OPEN || !this.connected) return
    if (this.muted) {
      // Keep the 60 ms cadence with silence rather than going quiet on the wire.
      ws.send(new ArrayBuffer(buffer.byteLength))
      return
    }
    ws.send(buffer)
  }

  /**
   * Peak-detect one outgoing frame. Pushed to the UI at most every
   * MIC_LEVEL_INTERVAL_MS (frames arrive at ~17 Hz — metering every one of them
   * would re-render the app for no visible gain), plus a 3 s rolling window
   * that flags a mic that is producing nothing but silence.
   */
  private meterFrame(buffer: ArrayBuffer): void {
    if (!this.options.onMicLevel) return
    const pcm = new Int16Array(buffer)
    let peak = 0
    // Every 4th sample is plenty for a meter and a quarter of the work.
    for (let i = 0; i < pcm.length; i += 4) {
      const v = Math.abs(pcm[i])
      if (v > peak) peak = v
    }
    if (peak > this.micPeak) this.micPeak = peak

    const now = Date.now()
    if (now - this.micWindowStart > MIC_SILENT_WINDOW_MS) {
      this.micSilent = !this.muted && this.micPeak < MIC_SILENT_PEAK
      this.micPeak = 0
      this.micWindowStart = now
    }
    if (now - this.lastMicLevelAt < MIC_LEVEL_INTERVAL_MS) return
    this.lastMicLevelAt = now
    // 16000 ≈ half scale — normal speech should fill most of the bar.
    this.options.onMicLevel(this.muted ? 0 : Math.min(1, peak / 16000), this.micSilent)
  }

  /** Release every resource. Never throws; safe to call twice. */
  private teardown(intentionalHangup = false): void {
    this.connected = false
    this.stopRingback()

    const ws = this.ws
    this.ws = null
    if (ws) {
      ws.onopen = null
      ws.onmessage = null
      ws.onerror = null
      ws.onclose = null
      try {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, intentionalHangup ? 'hangup' : 'detach')
        }
      } catch {
        // ignore
      }
    }

    if (this.capture) {
      this.capture.port.onmessage = null
      try {
        this.capture.port.postMessage({ type: 'stop' })
      } catch {
        // ignore
      }
    }
    if (this.playback) {
      try {
        this.playback.port.postMessage({ type: 'stop' })
      } catch {
        // ignore
      }
    }

    try {
      this.source?.disconnect()
      this.capture?.disconnect()
      this.playback?.disconnect()
      this.sink?.disconnect()
    } catch {
      // ignore
    }
    this.source = null
    this.capture = null
    this.playback = null
    this.sink = null

    // Stop the tracks BEFORE closing the context — closing first can leave the
    // browser's "recording" indicator lit until the tab is reloaded.
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        try {
          track.stop()
        } catch {
          // ignore
        }
      })
      this.stream = null
    }

    const ctx = this.ctx
    this.ctx = null
    if (ctx && ctx.state !== 'closed') {
      ctx.close().catch(() => undefined)
    }
  }

  private reportEnded(reason: string): void {
    if (this.endReported) return
    this.endReported = true
    this.options.onEnded?.(reason)
  }
}
